import React, { useState, useRef } from 'react';
import { adminApi, assessmentApi } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

const CSV_HEADERS = [
  'name', 'short_name', 'description', 'legal_name', 'registration_number',
  'address', 'city', 'country', 'phone', 'email', 'website',
  'contact_person_name', 'contact_person_title', 'contact_person_email', 'contact_person_phone',
];

const DISPLAY_COLUMNS = ['name', 'short_name', 'city', 'country', 'email', 'website'];

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map(line => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { values.push(current.trim()); current = ''; continue; }
      current += ch;
    }
    values.push(current.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    return row;
  }).filter(row => row.name);
}

function downloadTemplate() {
  const csv = CSV_HEADERS.join(',') + '\n' +
    'Example Energy Corp,EEC,National energy provider,Example Energy Corp Ltd,REG-12345,' +
    '123 Main St,Capital City,,+1-555-0100,info@example.com,https://www.example.com,' +
    'Jane Smith,Chief Technology Officer,jane.smith@example.com,+1-555-0101\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'oes_import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function BulkImportOESModal({ isOpen, onClose, assessments, defaultAssessmentId, onImported }) {
  const labels = useLabels();
  const t = useT();
  const fileRef = useRef();
  const [rows, setRows] = useState([]);
  const [assessmentId, setAssessmentId] = useState(defaultAssessmentId || '');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const role = localStorage.getItem('role') || '';
  const isGlobalAdmin = role === 'global_admin';

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target.result);
        if (parsed.length === 0) {
          setError('No valid rows found. Ensure the CSV has a header row and at least one data row with a name.');
          return;
        }
        setRows(parsed);
      } catch {
        setError('Failed to parse CSV file.');
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    if (!assessmentId && !isGlobalAdmin) {
      setError('Please select an assessment.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const res = await adminApi.bulkCreateOES({
        assessment_id: assessmentId ? Number(assessmentId) : null,
        items: rows,
      });
      setResult(res.data);
      if (onImported) onImported();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setRows([]);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/30 z-40" onClick={handleClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-eucnet-navy">{t('entity.bulkImportEntities', { entity: labels.entityNamePlural })}</h2>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Assessment selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('common.assessment')} *</label>
            <CustomSelect
              value={assessmentId}
              onChange={(e) => setAssessmentId(e.target.value)}
              options={(assessments || []).filter(a => a.name !== 'Default Assessment').map(a => ({ value: String(a.id), label: a.name }))}
              placeholder="Select assessment..."
              className="w-full border border-slate-300 rounded-lg text-sm"
            />
            {isGlobalAdmin && !assessmentId && (
              <p className="text-xs text-slate-400 mt-1">{t('entity.leaveEmptyDefault')}</p>
            )}
          </div>

          {/* Template download + file upload */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('entity.downloadCsvTemplate')}
            </button>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy cursor-pointer transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {t('entity.uploadCsv')}
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
            </label>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          {/* Preview table */}
          {rows.length > 0 && !result && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">{`${rows.length} ${labels.entityNamePlural} to import:`}</p>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">#</th>
                      {DISPLAY_COLUMNS.map(col => (
                        <th key={col} className="px-3 py-2 text-left font-semibold text-slate-600 capitalize">{col.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                        {DISPLAY_COLUMNS.map(col => (
                          <td key={col} className="px-3 py-2 text-slate-700 truncate max-w-[120px]" title={row[col]}>
                            {row[col] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">{t('entity.importComplete')}</p>
                <p className="text-green-700 text-sm mt-1">
                  {result.created?.length || 0} created, {result.errors?.length || 0} errors, {result.total} total
                </p>
              </div>
              {result.errors?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="font-medium text-amber-800 mb-1">Errors:</p>
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-amber-700">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={handleClose} className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">
            {result ? t('common.done') : t('common.cancel')}
          </button>
          {!result && rows.length > 0 && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy disabled:opacity-50"
            >
              {importing ? t('common.importing') : `${t('common.import')} ${rows.length} ${labels.entityNamePlural}`}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
